import axios from 'axios';
import appConfig from '../config/appConfig';

/**
 * Base de conocimiento global de Keenvil ([FAQ-ZENDESK], reemplaza al centro de ayuda de Zendesk).
 * townhall-api: /townhall/help/public (lectura sin login, solo lo publicado) y /townhall/help/staff (administracion,
 * solo staff de Keenvil). Con el flag apagado los endpoints no existen (404) y las secciones no se muestran.
 */
const IMAGES_PATH = '/townhall/help/public/images/';

function client(token, path) {
  const headers = {
    'X-API-Key': appConfig.apiKey,
    'X-Application-Id': 'CASTLEBLACK',
    'X-Device-OS': 'BROWSER',
    Accept: 'application/json'
  };
  if (token) {
    headers['X-Authorization'] = token;
  }
  return axios.create({
    baseURL: `${appConfig.getApiBaseUrl()}/townhall/help/${path}`,
    headers,
    timeout: 20000
  });
}

/** El back guarda las imagenes con ruta relativa a la API: para mostrarlas se completan con el host de la API. */
export function absoluteImages(html) {
  return (html || '')
    .split(`src="${IMAGES_PATH}`)
    .join(`src="${appConfig.getApiBaseUrl()}${IMAGES_PATH}`);
}

/** Inversa de absoluteImages: al guardar, las imagenes propias vuelven a ruta relativa (independiente del entorno). */
export function relativeImages(html) {
  return (html || '')
    .split(`${appConfig.getApiBaseUrl()}${IMAGES_PATH}`)
    .join(IMAGES_PATH);
}

// ---- lectura publica

/** El panel web es de la administracion: lee lo de ADMIN mas lo que no tiene audiencia. */
export const WEB_AUDIENCE = 'ADMIN';

let treeCache = null;

/** Arbol publicado { categories: [...] } o null si la base de conocimiento no esta habilitada. */
export function getPublicTree({ force = false } = {}) {
  if (!force && treeCache) {
    return treeCache;
  }
  treeCache = client(null, 'public')
    .get('/tree', { params: { audience: WEB_AUDIENCE } })
    .then(response => response.data)
    .catch(() => {
      treeCache = null;
      return null;
    });
  return treeCache;
}

export function getPublicArticle(articleId) {
  return client(null, 'public')
    .get(`/articles/${encodeURIComponent(articleId)}`, {
      params: { audience: WEB_AUDIENCE }
    })
    .then(response => ({
      ...response.data,
      body: absoluteImages(response.data.body)
    }));
}

export function searchArticles(q) {
  return client(null, 'public')
    .get('/search', { params: { q, audience: WEB_AUDIENCE } })
    .then(response => response.data || []);
}

// ---- administracion (staff)

let infoCache = null;

/** { staff } o null si no aplica (flag apagado, error). */
export function getHelpStaffInfo(token, { force = false } = {}) {
  if (!token) {
    return Promise.resolve(null);
  }
  if (!force && infoCache && infoCache.token === token) {
    return infoCache.promise;
  }
  const promise = client(token, 'staff')
    .get('/info')
    .then(response => response.data)
    .catch(() => null);
  infoCache = { token, promise };
  return promise;
}

export function getStaffTree(token) {
  return client(token, 'staff')
    .get('/tree')
    .then(response => response.data);
}

export function getStaffArticle(token, articleId) {
  return client(token, 'staff')
    .get(`/articles/${encodeURIComponent(articleId)}`)
    .then(response => ({
      ...response.data,
      body: absoluteImages(response.data.body)
    }));
}

const PATHS = {
  CATEGORY: 'categories',
  SECTION: 'sections',
  ARTICLE: 'articles'
};

/** Alta (sin id) o edicion de una categoria, seccion o articulo. */
export function saveItem(token, type, id, body) {
  const payload =
    type === 'ARTICLE' ? { ...body, body: relativeImages(body.body) } : body;
  const api = client(token, 'staff');
  const request = id
    ? api.put(`/${PATHS[type]}/${encodeURIComponent(id)}`, payload)
    : api.post(`/${PATHS[type]}`, payload);
  return request.then(response => response.data);
}

export function deleteItem(token, type, id) {
  return client(token, 'staff').delete(
    `/${PATHS[type]}/${encodeURIComponent(id)}`
  );
}

export function setPublished(token, articleId, published) {
  return client(token, 'staff')
    .post(
      `/articles/${encodeURIComponent(articleId)}/${
        published ? 'publish' : 'unpublish'
      }`
    )
    .then(response => response.data);
}

/**
 * Nuevo orden de hermanos: ids en el orden deseado. Los articulos se ordenan dentro de su
 * seccion (un articulo puede estar en varias).
 */
export function saveOrder(token, type, ids, sectionId) {
  return client(token, 'staff').put(
    '/order',
    sectionId ? { type, ids, sectionId } : { type, ids }
  );
}

/** Sube una imagen y devuelve la URL absoluta para insertarla en el articulo. */
export function uploadImage(token, file) {
  const data = new FormData();
  data.append('file', file);
  return client(token, 'staff')
    .post('/images', data)
    .then(response => {
      const url = response.data.url || '';
      return url.startsWith('/') ? `${appConfig.getApiBaseUrl()}${url}` : url;
    });
}

/**
 * Importa (o re-sincroniza, es idempotente) el centro de ayuda de Zendesk. Baja ~36 imagenes: puede tardar mas de un
 * minuto. Devuelve { categoriasNuevas, seccionesNuevas, articulosNuevos, ...Actualizados, imagenes }.
 */
export function importZendesk(token, publish) {
  return client(token, 'staff')
    .post('/import/zendesk', null, {
      params: { publish: !!publish },
      timeout: 300000
    })
    .then(response => response.data);
}

/** Mensaje legible de un error del back ({ message }) o de red. */
export function errorMessage(error) {
  const data = error && error.response && error.response.data;
  if (data && data.message) {
    return data.message;
  }
  return 'No se pudo completar la operación. Probá de nuevo en unos minutos.';
}
